import { DeviceStatusCard } from './Cards/DeviceStatusCard';
import { VodPurchasesCard } from './Cards/VodPurchasesCard';
import { MessagesCard } from './Cards/MessagesCard';
import { CheckoutsCard } from './Cards/CheckoutsCard';
import { TopDetailPagesCard } from './Cards/TopDetailPagesCard';
import { SignageCard } from './Cards/SignageCard';
import { LateCheckoutsCard } from './Cards/LateCheckoutsCard';
import { ScheduledAdsCard } from './Cards/ScheduledAdsCard';
import { Permissions } from '../../types/commonTypes';
import { DevicesCard } from './Cards/DevicesCard';
import { AccountCard } from './Cards/AccountCard';
import { useOperator } from '../../contexts/OperatorContext';
import { useAppView } from '../../contexts/AppViewContext';

const defaultCardSize = {
  size: { w: 1, h: 3 },
  minSize: { minW: 1, minH: 3 },
};

export const useDashboardCards = (
  permissions: Permissions,
  siteId: number,
  systems: string[],
  type: OperatorType | null,
  theme: string[]
) => {
  const { transports } = useOperator();
  const appView = useAppView();

  const isMR =
    systems.includes('MediaRoom') || systems.includes('Residential MediaRoom');
  const isMF =
    systems.includes('MediaFirst') ||
    systems.includes('Residential MediaFirst');
  const isEvolve = systems.includes('Evolve');
  const isDS = systems.includes('Digital Signage');
  const isFTG = systems.includes('FTG');
  const isWiFi = systems.includes('Guest Internet');
  const isGRMS = systems.includes('Guest Room Management');
  const isDOCSIS = (transports ?? []).some(
    (t) => t.active && t.name === 'DOCSIS'
  );
  const isRes = type === 'Residential';
  const isHos = type === 'Hospitality';

  if (appView.type !== 'Operator') return [];

  return [
    {
      id: 'STBStatus',
      permission: isMR,
      component: <DeviceStatusCard deviceType="STB" theme={theme} />,
      defaultSize: defaultCardSize,
    },
    {
      id: 'Devices',
      permission: isMR && isRes,
      component: <DevicesCard theme={theme} />,
      defaultSize: defaultCardSize,
    },
    {
      id: 'Account',
      permission: (isMR || isMF) && isRes,
      component: <AccountCard theme={theme} />,
      defaultSize: defaultCardSize,
    },
    {
      id: 'ModemStatus',
      permission: isMR && isDOCSIS,
      component: <DeviceStatusCard deviceType="Modem" theme={theme} />,
      defaultSize: defaultCardSize,
    },
    {
      id: 'VodPurchases',
      permission: permissions.canHosControlProperty && isMR && isHos,
      component: <VodPurchasesCard theme={theme} />,
      defaultSize: defaultCardSize,
    },
    {
      id: 'Checkouts',
      permission: permissions.canHosMenuHits && (isMR || isEvolve) && isHos,
      component: (
        <CheckoutsCard theme={theme} hideLateCheckout={isEvolve && !isMR} />
      ),
      defaultSize: defaultCardSize,
    },
    {
      id: 'LateCheckouts',
      permission: permissions.canHosLateCheckoutView && isMR && isHos,
      component: <LateCheckoutsCard theme={theme} />,
      defaultSize: defaultCardSize,
    },
    {
      id: 'TopDetailPages',
      permission: permissions.canHosMenuHits && (isMR || isEvolve) && isHos,
      component: <TopDetailPagesCard theme={theme} />,
      defaultSize: defaultCardSize,
    },
    {
      id: 'Messaging',
      permission: permissions.canHosMessagingView && isMR,
      component: <MessagesCard isRes={isRes} theme={theme} />,
      defaultSize: defaultCardSize,
    },
    {
      id: 'ScheduledAds',
      permission: permissions.canHosAdScheduler && isMR && isHos,
      component: <ScheduledAdsCard theme={theme} />,
      defaultSize: defaultCardSize,
    },
    {
      id: 'Signage',
      permission: isDS && isHos,
      component: <SignageCard theme={theme} />,
      defaultSize: defaultCardSize,
    },
  ];
};
